import { axiosApiInstance } from "../../../pages/api/axios";
import {
  ACTIONS_CONFIG,
  CONDITIONS_CONFIG,
  FEActionResourceType,
  requiredTriggerConditions,
  RESOURCE_TYPES,
} from "../constants/resourceTypes";
import _set from "lodash/set";
import { generateTempNode, getDefaultEdgeProps } from "../utils";
import {
  RoutingAccount,
  RoutingAction,
  RoutingCondition,
  RoutingRulesResponse,
  ServerAction,
  ServerBranchActionData,
} from "../types/api";
import { Flow } from "../../../components/PaymentFlows/types";
import { RoutingGraph } from "./graph.service";
import {
  ActionNode,
  AutomationTaskNode,
  BranchActionData,
  JumpActionData,
  NodeType,
  TempNode,
  TriggerCondition,
  TriggerNode,
} from "../types/nodes";
import { EdgeType, FlowEdge, SourceHandle } from "../types/edges";
import { getNodeConnectionButtons } from "../components/nodes/ActionNode/utils";

export class RoutingResource {
  static async listConditions(options: {
    flowId: string;
    method: string;
    signal: AbortSignal;
  }) {
    return axiosApiInstance
      .get<{ data: RoutingCondition[] }>(
        `/d/flows/${options.flowId}/conditions/${options.method}/`,
        { signal: options.signal }
      )
      .then((response) => {
        const data = response?.data?.data
          ?.filter((condition) =>
            Boolean(CONDITIONS_CONFIG[condition.resource_type])
          )
          ?.map((condition) => ({
            ...CONDITIONS_CONFIG[condition.resource_type],
            results: condition.results,
            nodeTypes: condition.node_type,
            s2s: condition.for_server_to_server,
          }));

        return data;
      });
  }

  static async listRequiredData(options: { flowId: string; method: string }) {
    // TS_TODO: get required data payload type
    return axiosApiInstance
      .get(`/d/flows/${options.flowId}/required_data/${options.method}/`)
      .then((response) => response?.data);
  }

  static async listActions(options: {
    flowId: string;
    method: string;
    currencies: string[];
    controller: AbortController;
  }) {
    return axiosApiInstance
      .get<{ data: RoutingAction[] }>(
        `/d/flows/${options.flowId}/actions/${options.method}/`,
        {
          params: { currencies: options.currencies.join(",") },
          signal: options.controller?.signal,
        }
      )
      .then((response) => {
        const data = response?.data?.data;
        const availableActions = data.map((action) => ({
          ...ACTIONS_CONFIG[action.resource_type],
          results: action.results,
        }));

        return availableActions;
      });
  }

  static async listAccounts(accounts: string[] = []) {
    return axiosApiInstance
      .get<{ data: RoutingAccount[] }>(
        `/d/accounts/utils/?accounts=${accounts.join(",")}`
      )
      .then((response) => {
        const data = response?.data?.data;

        return data;
      });
  }

  static async createFlow(options: {
    name: string;
    accounts: string[];
    type: "PayIn";
    isLive?: boolean;
  }) {
    return axiosApiInstance
      .post<{ data: Flow }>(`/d/flows/`, {
        name: options.name,
        accounts: options.accounts,
        type: options.type,
        is_live: options.isLive,
      })
      .then((response) => {
        const data = response?.data?.data;

        return data;
      });
  }

  static async updateFlow(options: {
    id: string;
    accounts: string[];
    name: string;
    type?: string;
    isLive?: boolean;
  }) {
    return axiosApiInstance
      .patch(`/d/flows/${options.id}/`, {
        name: options.name,
        accounts: options.accounts,
        type: options.type,
      })
      .then((response) => {
        const data = response?.data?.data;

        return data;
      });
  }

  static async listFlowVersions(flowId: string) {
    return axiosApiInstance
      .get(`/d/flows/${flowId}/versions/`)
      .then((res) => res.data);
  }

  static async updateFlowVersion(options: {
    flowId: string;
    versionNumber: string;
    name: string;
    notes: string;
  }) {
    const { flowId, versionNumber, name, notes } = options;

    return axiosApiInstance
      .patch(
        `/d/flows/${flowId}/versions/`,
        { name, notes },
        { params: { version_number: versionNumber } }
      )
      .then((res) => res.data.data);
  }

  static async duplicateFlowVersion(options: {
    flowId: string;
    versionNumber: string;
  }) {
    const { flowId, versionNumber } = options;

    return axiosApiInstance
      .post(
        `/d/flows/${flowId}/versions/duplicate/`,
        {},
        { params: { version_number: versionNumber } }
      )
      .then((res) => res.data.data);
  }

  static async publishFlow(
    flowId: string,
    payload?: { skipValidation?: boolean }
  ) {
    return axiosApiInstance.post(`/d/flows/${flowId}/publish/`, {
      skip_validation: payload?.skipValidation,
    });
  }

  static async deleteFlow(flowId: string) {
    return axiosApiInstance.delete(`/d/flows/${flowId}/delete/`);
  }

  static async getRoutingRule(options: {
    paymentMethod: string;
    flowId: string;
    versionNumber: string;
    signal: AbortSignal;
  }) {
    const { paymentMethod, flowId, versionNumber } = options;

    return axiosApiInstance
      .get(`/d/flows/${flowId}/rules/${paymentMethod}/`, {
        params: { version_number: versionNumber },
        signal: options.signal,
      })
      .then((response) => {
        const data = response?.data?.data;
        const routingGraph = this.#generateClientResource(data);
        return routingGraph;
      });
  }

  static async getRoutingUIConfig(options: {
    paymentMethod: string;
    flowId: string;
    versionNumber: string;
  }) {
    const { paymentMethod, flowId, versionNumber } = options;

    return axiosApiInstance
      .get(`/d/flows/${flowId}/${paymentMethod}/ui_config/`, {
        params: { version_number: versionNumber },
      })
      .then((response) => {
        const data = response?.data?.data;
        return data.canvas_details;
      })
      .catch(() => {
        return {};
      });
  }

  static async updateRoutingRule(options: {
    paymentMethod: string;
    flowId: string;
    routingGraph: RoutingGraph;
    versionNumber: string;
  }) {
    const { paymentMethod, flowId, routingGraph, versionNumber } = options;
    const resource = this.#generateServerResource(routingGraph);
    const updatedRouteDetails = await axiosApiInstance
      .patch(`/d/flows/${flowId}/rules/${paymentMethod}/`, resource, {
        params: { version_number: versionNumber },
      })
      .then((response) => {
        const data = response?.data?.data;
        return data;
      });

    return updatedRouteDetails;
  }

  /**
   * Private methods
   */

  static #generateServerResource(routingGraph: RoutingGraph) {
    if (!routingGraph) {
      throw new Error("No routingGraph was provided when calling the method");
    }
    const triggers = routingGraph.triggers;
    const actions = routingGraph.nodes.filter(
      (node): node is ActionNode => node.type === NodeType.Action
    );
    const connectionEdges = routingGraph.edges.filter(
      (edge) => edge.data?.type === "connection"
    );

    const getTriggerConditions = (triggerNode: TriggerNode) => {
      const triggerConditions = Object.values(
        triggerNode.data?.appliedConditions ?? {}
      );

      return triggerConditions
        .filter((condition) => Boolean(condition.data))
        .map((condition) => ({
          resource_type: RESOURCE_TYPES.conditions[condition.resourceType],
          reference: condition.id,
          resource_data: condition.data,
        }));
    };

    return {
      triggers: triggers.map((triggerNode) => {
        return {
          resource_type: triggerNode.data.resourceType,
          reference: triggerNode.id,
          alias: triggerNode.data.alias,
          frontend_trigger_type: triggerNode.data.frontendTriggerType,
          conditions: getTriggerConditions(triggerNode),
          connection:
            connectionEdges.find((edge) => edge.source === triggerNode.id)
              ?.target ?? null,
        };
      }),
      actions: actions.map((actionNode) => {
        const tasks = actionNode.data.tasks;
        const sourceConnectionNodeId =
          tasks && tasks.length > 0
            ? tasks[tasks.length - 1].reference
            : actionNode.id;

        const appliedAction = actionNode.data.appliedAction;
        const connectedNodes = connectionEdges.filter(
          (edge) =>
            edge.source === sourceConnectionNodeId &&
            !edge.target.startsWith("temp")
        );

        const connections = connectedNodes.reduce(
          (connections: Record<string, string>, current) => {
            connections[current.sourceHandle!] = current.target;
            return connections;
          },
          {}
        );

        const isConnected = Object.keys(connections).length > 0;
        return {
          reference: actionNode.id,
          alias: actionNode.data.alias,
          resource_type: RESOURCE_TYPES.actions[appliedAction.resourceType],
          resource_data:
            this.#mapActionDataToServer(
              appliedAction.resourceType,
              appliedAction.data
            ) ?? {},
          connections: isConnected ? connections : null,
          tasks: tasks || undefined,
        };
      }),
      default_operation: routingGraph.defaultOperation,
    };
  }

  static #generateClientResource(serverResource: RoutingRulesResponse) {
    if (!serverResource) {
      throw new Error("No serverResource was provided when calling the method");
    }

    const triggers = serverResource.triggers.map((trigger) => {
      const triggerLabel = `Trigger ${trigger.reference.slice(0, 4)}`;

      const getTriggerConditions = () => {
        return trigger.conditions.reduce(
          (object: Record<string, TriggerCondition>, condition) => {
            const conditionConfig = CONDITIONS_CONFIG[condition.resource_type];
            const isConditionLocked = requiredTriggerConditions.includes(
              conditionConfig.resourceType
            );

            object[condition.reference] = {
              id: condition.reference,
              data: condition.resource_data,
              resourceType: conditionConfig.resourceType,
              label: conditionConfig.label,
              type: conditionConfig.type,
              isLocked: isConditionLocked,
            };

            return object;
          },
          {}
        );
      };
      return {
        id: trigger.reference,
        type: "trigger" as NodeType.Trigger,
        data: {
          label: triggerLabel,
          alias:
            trigger.alias === `Trigger ${trigger.reference}`
              ? "Untitled Trigger"
              : trigger.alias,
          frontendTriggerType: trigger.frontend_trigger_type,
          resourceType: trigger.resource_type,
          isLocked: false,
          isConnecting: false,
          appliedConditions: getTriggerConditions(),
          connection: trigger.connection,
        },
        position: {
          x: 0,
          y: 0,
        },
        draggable: false,
      };
    });

    /**
     * format all [actionNodes, taskNodes]
     */
    const {
      actionNodes,
      taskNodes,
      tempNodes = [],
    } = serverResource.actions.reduce(
      (acc, action) => {
        const actionConfig = ACTIONS_CONFIG[action.resource_type];
        // Actual Node
        const actionNode = {
          id: action.reference,
          type: "action" as NodeType.Action,
          data: {
            label: actionConfig.label,
            alias:
              action.alias === `Action ${action.reference}` ||
              action.alias === actionConfig.oldLabel
                ? actionConfig.label
                : action.alias,
            appliedAction: {
              data: this.#mapActionDataToClient(
                action.resource_type,
                action.resource_data
              ),
              label: actionConfig.label,
              resourceType: actionConfig.resourceType,
            },
            isConnecting: false,
            resourceType: action.resource_type,
            connections: action.connections,
            tasks: action.tasks,
          },
          position: {
            x: 0,
            y: 0,
          },
          draggable: false,
        };

        let calculatedActionConnections: Record<string, string> = {};
        const allowedConnections = getNodeConnectionButtons(actionNode);
        // Temp Nodes
        if (allowedConnections?.length) {
          const tempNodes = allowedConnections.reduce(
            (acc, { sourceHandle }) => {
              if (!action.connections?.[sourceHandle]) {
                const tempNode = generateTempNode(actionConfig.resourceType);
                acc.push(tempNode);
                calculatedActionConnections[sourceHandle] = tempNode.id;
              } else {
                calculatedActionConnections[sourceHandle] =
                  action.connections[sourceHandle];
              }

              return acc;
            },
            [] as TempNode[]
          );

          acc.tempNodes.push(...tempNodes);
        }

        actionNode.data.connections =
          actionNode.data.appliedAction.resourceType === "jump"
            ? null
            : calculatedActionConnections;
        acc.actionNodes.push(actionNode);

        // Task Nodes
        if (action.tasks && action.tasks.length > 0) {
          const taskNodes = action.tasks.map((task) => ({
            id: task.reference,
            type: "automationTask" as NodeType.AutomationTask,
            data: {
              resourceType: task.resource_type,
              actionNodeId: actionNode.id,
              alias:
                task.alias === `Action ${task.reference}`
                  ? ACTIONS_CONFIG[
                      task.resource_type as keyof typeof ACTIONS_CONFIG
                    ].label
                  : task.alias,
              resource_data: task.resource_data,
            },
            position: {
              x: 0,
              y: 0,
            },
            draggable: false,
          }));
          acc.taskNodes.push(...taskNodes);
        }

        return acc;
      },
      { actionNodes: [], taskNodes: [], tempNodes: [] } as {
        actionNodes: ActionNode[];
        taskNodes: AutomationTaskNode[];
        tempNodes: TempNode[];
      }
    );

    const allNodes = [...triggers, ...actionNodes, ...taskNodes, ...tempNodes];

    const edges: FlowEdge[] = [];

    allNodes.forEach((node) => {
      if (node.type === NodeType.Trigger && node.data.connection) {
        edges.push({
          id: `e${"triggers"}-${node.id}-${node.data.connection}`,
          type: EdgeType.DefaultEdge,
          source: node.id,
          target: node.data.connection,
          ...getDefaultEdgeProps({
            sourceNode: node,
            isConnectingToTrigger: true,
          }),
        });

        return;
      }
      /**
       * If action has tasks
       *  - Generate all edges between the node & it's tasks
       *    - node -> task1 | task1 -> task2 | task2 -> task3 | ...
       *  - For connections add it to last task if exist
       *    - lastTask -> connections
       */
      if (node.type === NodeType.Action) {
        const tasks = node.data.tasks;
        if (tasks && tasks.length > 0) {
          tasks.forEach((task, index) => {
            const isFirstTask = index === 0;
            const isLastTask = index === tasks.length - 1;

            if (isFirstTask) {
              edges.push({
                id: `e-${node.id}-${task.reference}`,
                type: EdgeType.DefaultEdge,
                source: node.id,
                target: task.reference,
                ...getDefaultEdgeProps({
                  sourceNode: node,
                  isConnectingToTrigger: false,
                }),
              });
            }

            if (isLastTask) return;

            const nextTask = tasks[index + 1];
            edges.push({
              id: `e-${task.reference}-${nextTask.reference}`,
              type: EdgeType.DefaultEdge,
              source: task.reference,
              target: nextTask.reference,
              ...getDefaultEdgeProps({
                sourceNode: node,
                isConnectingToTrigger: false,
              }),
            });
          });
        }

        if (node.data.connections) {
          Object.entries(node.data.connections).forEach(
            ([sourceHandle, connection]) => {
              // Connect the connections to latest task if has any
              if (tasks && tasks.length > 0) {
                const lastTask = tasks[tasks.length - 1];
                edges.push({
                  id: `e${lastTask.id}-${connection}-${sourceHandle}`,
                  type: EdgeType.DefaultEdge,
                  source: lastTask.reference,
                  sourceHandle: sourceHandle,
                  target: connection,

                  ...getDefaultEdgeProps({
                    sourceHandle: sourceHandle as SourceHandle,
                    sourceNode: node,
                    isConnectingToTrigger: false,
                  }),
                });
              } else {
                edges.push({
                  id: `e${node.id}-${connection}-${sourceHandle}`,
                  type: EdgeType.DefaultEdge,
                  source: node.id,
                  sourceHandle: sourceHandle,
                  target: connection,
                  ...getDefaultEdgeProps({
                    sourceHandle: sourceHandle as SourceHandle,
                    sourceNode: node,
                    isConnectingToTrigger: false,
                  }),
                });
              }
            }
          );
        }

        if (
          node.type === NodeType.Action &&
          node.data.appliedAction.resourceType === "jump"
        ) {
          const jumpTarget = (node.data.appliedAction?.data as JumpActionData)
            ?.to_action_reference;
          if (jumpTarget) {
            edges.push({
              id: `e${node.id}-${jumpTarget}`,
              type: EdgeType.SmartJumpEdge,
              source: node.id,
              target: jumpTarget,
              style: { strokeWidth: 1.5, strokeDasharray: "4,4" },
              data: {
                type: "jump",
                isJumpEdge: true,
              },
              animated: true,
            });
          }
        }
      }
    });

    // add meta data connection source node id for each node
    allNodes.forEach((node) => {
      const sourceNodeId = edges.find((edge) => edge.target === node.id)
        ?.source;
      if (sourceNodeId) _set(node, "data.meta.sourceNodeId", sourceNodeId);
    });

    return {
      nodes: allNodes,
      edges,
      id: serverResource.id,
      flowId: serverResource.flow_id,
      flowStatus: (serverResource as any).flow_status, // TS_TODO: duple check flow_status as it is not in the server response
      paymentMethod: serverResource.payment_method,
      defaultOperation: serverResource.default_operation,
    };
  }

  static #mapActionDataToServer(
    actionType: FEActionResourceType,
    actionData: ActionNode["data"]["appliedAction"]["data"]
  ) {
    if (actionType === "branch") {
      return Object.values((actionData as BranchActionData) ?? {}).map(
        (branch) => {
          return {
            reference: branch.id,
            resource_data: Object.values(branch?.data ?? {}).map(
              (condition) => ({
                reference: condition.id,
                resource_data: condition.data,
                resource_type:
                  RESOURCE_TYPES.conditions[condition.resourceType],
              })
            ),
            alias: branch.alias,
          };
        }
      );
    }

    // Actions that don't need mapping
    return actionData;
  }

  static #mapActionDataToClient(
    actionType: ServerAction["resource_type"],
    actionData: ServerAction["resource_data"]
  ) {
    if (actionType === RESOURCE_TYPES.actions["branch"]) {
      return Object.fromEntries(
        (actionData as ServerBranchActionData[]).map((branch) => [
          branch.reference,
          {
            id: branch.reference,
            alias:
              branch.alias === "Branch"
                ? `${branch.reference.slice(0, 10)}`
                : branch.alias,
            data: Object.fromEntries(
              branch.resource_data.map((condition) => [
                condition.reference,
                {
                  id: condition.reference,
                  label: CONDITIONS_CONFIG[condition.resource_type].label,
                  resourceType:
                    CONDITIONS_CONFIG[condition.resource_type].resourceType,
                  data: condition.resource_data,
                },
              ])
            ),
          },
        ])
      );
    }

    // Actions that don't need mapping
    return actionData;
  }
}
